import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { showErrorToast, showSuccessToast } from '../../actions/notification-actions';
import { GENERIC_ERROR_TOAST_TITLE, isBadRequest, resolveInlineListNameError } from './listErrors';

const ENTER_KEY = 'Enter';

// Shared name/save orchestration for the SavedLists name-entry modals (CreateListModal,
// EditListNameModal): holds the name + inline error + saving flag, runs the submit thunk, maps a 400
// to an inline message (else an error toast), and on success closes, toasts, and hands the trimmed
// name back to the caller. Extracted alongside useListPicker so the two name modals don't duplicate
// this flow; each modal keeps its own JSX (testids and copy differ by surface) and supplies:
//   onSubmit(trimmedName) -> Promise   the create/rename thunk dispatch
//   onSubmitted(trimmedName)           post-success side effect (heading update / onSuccess re-fetch)
//   buildSuccessToast(trimmedName)     the success toast payload
//   genericInlineMessage / genericToastMessage
//
// Save is enabled only for a non-empty name that differs from the initial one — for a fresh create
// (initialName '') that reduces to "non-empty"; for a rename it also blocks a no-op PUT + "renamed"
// toast on an unchanged name.
const useListNameForm = ({
    initialName = '',
    onCloseClick,
    onSubmit,
    onSubmitted,
    buildSuccessToast,
    genericInlineMessage,
    genericToastMessage,
}) => {
    const dispatch = useDispatch();
    const [name, setName] = useState(initialName);
    const [nameError, setNameError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const trimmedName = name.trim();
    const isSaveEnabled = trimmedName.length > 0 && trimmedName !== initialName.trim();

    const handleNameChange = (e) => {
        setName(e.target.value);
        setNameError(null);
    };

    const handleSave = async () => {
        if (!isSaveEnabled || isSaving) {
            return;
        }
        setIsSaving(true);
        setNameError(null);
        // The try only wraps the submit call itself — anything thrown by the post-success work
        // (closing, toasting, onSubmitted) must never be reported as a failed submit, which would
        // invite a duplicate re-submit of a name that was actually saved.
        try {
            await onSubmit(trimmedName);
        } catch (error) {
            if (isBadRequest(error)) {
                setNameError(resolveInlineListNameError(error, genericInlineMessage));
            } else {
                dispatch(showErrorToast({ title: GENERIC_ERROR_TOAST_TITLE, message: genericToastMessage }));
            }
            // Only reset on failure — on success onCloseClick() below unmounts the modal, so resetting
            // after that point would set state on an unmounted node.
            setIsSaving(false);
            return;
        }

        onCloseClick();
        dispatch(showSuccessToast(buildSuccessToast(trimmedName)));
        if (onSubmitted) {
            onSubmitted(trimmedName);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === ENTER_KEY) {
            handleSave();
        }
    };

    return { name, nameError, isSaving, isSaveEnabled, handleNameChange, handleSave, handleKeyDown };
};

export default useListNameForm;
